import * as React from "react";

import { cn } from "@/lib/utils";
import { DictateButton } from "@/components/DictateButton";

export type TextareaProps = React.ComponentProps<"textarea"> & {
  /** Set false to hide the voice-dictation microphone on this field. */
  dictation?: boolean;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, dictation, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );

    const canDictate = (dictation ?? true) && !props.disabled && !props.readOnly;

    const field = (
      <textarea
        className={cn(
          "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          canDictate && "pr-11",
          className,
        )}
        ref={setRefs}
        {...props}
      />
    );

    if (!canDictate) return field;

    return (
      <div className="relative w-full">
        {field}
        <DictateButton targetRef={innerRef} className="absolute right-2 bottom-2" />
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
